import { useState } from 'react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../design-system/primitives/dialog';
import { ActionButton } from '../../design-system/concept/action-button';
import { Eyebrow } from '../../design-system/concept/eyebrow';
import { useToast } from '../../design-system/primitives/use-toast';
import {
  ConfirmedKey,
  MeasureData,
  MeasurementKey,
} from '../../lib/interfaces';
import { feedbackMeasurements, scannedValue } from '../../lib/dashboard/feedback';
import { hasFeature } from '../../lib/features';
import {
  BODY_FAT_METHODS,
  CLOTHING_CHOICES,
  MAX_TEXT,
  OTHER,
  contextChanged,
  draftFrom,
  lastMeasurerName,
  problemWith,
  rememberMeasurerName,
  toPayload,
  type Choice,
  type ContextDraft,
} from '../../lib/dashboard/feedbackContext';

/** Weight, height and body fat are not tape readings off the mesh. */
const NOT_A_TAPE_READING = ['weight', 'height', 'body_fat'];

interface FeedbackDialogProps {
  measureData: MeasureData;
  gender: 'male' | 'female';
  onUpdateMeasurement: (measurement: MeasureData) => void | Promise<void>;
  /**
   * The button that opens it. Settings uses the default "Review"; the phone's
   * scan rows pass their own, sized for a thumb.
   */
  trigger?: React.ReactNode;
}

const TEXT_INPUT =
  'w-full rounded-md border border-border bg-card px-2 py-1.5 text-xs outline-none focus:border-primary';

/** A short list of options, or Other with the coach's own words. */
const ChoiceField = ({
  label,
  hint,
  choices,
  value,
  text,
  onChange,
}: {
  label: string;
  hint?: string;
  choices: Choice[];
  value: string;
  text: string;
  onChange: (value: string, text: string) => void;
}) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-xs font-medium">
      {label}
      {hint && <span className="ml-1 font-normal text-muted-foreground">({hint})</span>}
    </span>
    <select
      value={value}
      aria-label={label}
      onChange={(e) => onChange(e.target.value, e.target.value === OTHER ? text : '')}
      className={TEXT_INPUT}
    >
      <option value="">Not recorded</option>
      {choices.map((c) => (
        <option key={c.value} value={c.value}>
          {c.label}
        </option>
      ))}
      <option value={OTHER}>Other…</option>
    </select>
    {value === OTHER && (
      <input
        type="text"
        value={text}
        maxLength={MAX_TEXT}
        placeholder="Describe"
        aria-label={`${label}, in your words`}
        onChange={(e) => onChange(OTHER, e.target.value)}
        className={TEXT_INPUT}
      />
    )}
  </label>
);

/**
 * Body fat from another method — InBody, DEXA and the like — never a
 * thumbs-up on the app's estimate. Optional; once a value is entered, the
 * method is required (functions-api #22 refuses one without).
 */
const BodyFatRow = ({
  scanned,
  stored,
  editing,
  editValue,
  onEditValue,
  onStartEdit,
  onCommit,
  onCancel,
  onUndo,
  method,
  methodText,
  onMethod,
}: {
  scanned?: number;
  stored?: number;
  editing: boolean;
  editValue: string;
  onEditValue: (v: string) => void;
  onStartEdit: () => void;
  onCommit: () => void;
  onCancel: () => void;
  onUndo: () => void;
  method: string;
  methodText: string;
  onMethod: (value: string, text: string) => void;
}) => (
  <div data-testid="body-fat-feedback" className="mt-2 flex flex-col gap-2 rounded-lg border border-border p-3">
    {/* Stacked on a phone: beside an input and two buttons the label
        column was a word wide. */}
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
      <div className="min-w-0">
        <div className="text-xs font-medium">
          Body fat <span className="font-normal text-muted-foreground">(optional)</span>
        </div>
        <div className="text-[10px] text-muted-foreground">
          From another method, e.g. InBody or DEXA
        </div>
        <div className="tabular text-[10px] text-muted-foreground">
          {scanned !== undefined ? `App estimate ${scanned.toFixed(1)} %` : 'No estimate in this scan'}
        </div>
      </div>
      {editing ? (
        <div className="flex shrink-0 items-center gap-2">
          <input
            type="number"
            autoFocus
            min={2}
            max={70}
            step={0.1}
            value={editValue}
            aria-label="Body fat in %"
            onChange={(e) => onEditValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') onCommit();
              if (e.key === 'Escape') onCancel();
            }}
            className="tabular w-20 rounded-md border border-border bg-card px-2 py-1 text-xs outline-none focus:border-primary"
          />
          <span className="text-[10px] text-muted-foreground">%</span>
          <ActionButton variant="primary" ariaLabel="Save Body fat" onClick={onCommit}>
            Save
          </ActionButton>
          <ActionButton onClick={onCancel}>Cancel</ActionButton>
        </div>
      ) : stored !== undefined ? (
        <div className="flex shrink-0 items-center gap-2">
          <span className="tabular text-xs font-semibold">{stored} %</span>
          <ActionButton ariaLabel="Change Body fat" onClick={onStartEdit}>
            Change
          </ActionButton>
          <ActionButton ariaLabel="Remove Body fat" onClick={onUndo}>
            Undo
          </ActionButton>
        </div>
      ) : (
        <ActionButton ariaLabel="Enter Body fat" onClick={onStartEdit} className="self-start sm:self-auto">
          Enter
        </ActionButton>
      )}
    </div>
    {stored !== undefined && (
      <ChoiceField
        label="Body fat method"
        hint="required"
        choices={BODY_FAT_METHODS}
        value={method}
        text={methodText}
        onChange={onMethod}
      />
    )}
  </div>
);

/**
 * Review the tape readings from one scan: confirm each as measured, or correct
 * it to what the tape actually said.
 *
 * The state model is the subtle part and is unchanged. A reading is
 * "confirmed" when the coach has agreed the scanned value is right (the
 * confirmed value equals the original), and "corrected" when they have
 * replaced it with something else. Both land in `confirmedMeasurements`; what
 * separates them is whether the stored number matches the scan.
 */
export const FeedbackDialog = ({
  measureData,
  gender,
  onUpdateMeasurement,
  trigger,
}: FeedbackDialogProps) => {
  const { toast } = useToast();
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  // Cloned so nothing is written back until Confirm changes.
  const [draft, setDraft] = useState<MeasureData>({ ...measureData });
  // Who measured, clothing, and the body fat method (feedbackContext.ts).
  const [ctx, setCtx] = useState<ContextDraft>(() =>
    draftFrom(measureData.feedbackContext, lastMeasurerName()),
  );
  const editCtx = (patch: Partial<ContextDraft>) => setCtx((prev) => ({ ...prev, ...patch }));

  // Both waists for every client, named by site: the tape sheet takes the
  // waist across the navel and at the narrowest point on everyone (SF-87).
  const keys = feedbackMeasurements(gender).filter((k) => !NOT_A_TAPE_READING.includes(k.key));

  const readingFor = (key: string) => {
    // Undefined when this scan has no value for the site: data.ts turns a
    // missing waist_female into 0, which is not a reading to confirm.
    const original = scannedValue(
      measureData.measurements[key as keyof typeof measureData.measurements],
    );
    const stored = draft.confirmedMeasurements
      ? (draft.confirmedMeasurements[key as ConfirmedKey] as number | undefined)
      : undefined;

    return {
      original,
      stored,
      // Agreed with the scan.
      confirmed: original !== undefined && stored === original,
      // Replaced with a tape reading that differs from the scan.
      corrected: stored !== undefined && stored !== original,
    };
  };

  // Body fat is not a tape reading and is never "confirmed": agreeing with the
  // app's own estimate measures nothing. Only a value from another method,
  // with that method named, is feedback.
  const bodyFatScanned = scannedValue(measureData.measurements.body_fat);
  const bodyFatBefore = measureData.confirmedMeasurements?.body_fat;
  const bodyFatAfter = draft.confirmedMeasurements?.body_fat;

  const readingsChanged = [...keys.map((k) => k.key), 'body_fat'].some((key) => {
    const stored = draft.confirmedMeasurements?.[key as ConfirmedKey];
    const was = measureData.confirmedMeasurements?.[key as ConfirmedKey];
    return stored !== was;
  });
  const hasChanges =
    readingsChanged || contextChanged(ctx, measureData.feedbackContext, bodyFatAfter !== undefined);
  const problem = problemWith(ctx, { before: bodyFatBefore, after: bodyFatAfter });

  const save = async () => {
    if (ctx.measuredBy === 'other') rememberMeasurerName(ctx.measurerName);
    try {
      await onUpdateMeasurement({
        ...draft,
        feedbackContext: toPayload(ctx, bodyFatAfter !== undefined),
      });
      toast({
        title: 'Readings saved',
        description: 'Confirmed and corrected readings have been stored.',
      });
    } catch (e) {
      toast({
        title: 'Not saved',
        description: e instanceof Error ? e.message : 'The readings did not save. Try again.',
        variant: 'destructive',
      });
    }
  };

  const setStored = (key: MeasurementKey, value: number) =>
    setDraft((prev) => ({
      ...prev,
      confirmedMeasurements: {
        ...prev.confirmedMeasurements,
        [key]: value,
      } as MeasureData['confirmedMeasurements'],
    }));

  const clearStored = (key: MeasurementKey) =>
    setDraft((prev) => {
      if (!prev.confirmedMeasurements) return prev;
      const next = { ...prev.confirmedMeasurements };
      delete next[key as ConfirmedKey];
      return { ...prev, confirmedMeasurements: next };
    });

  const commitEdit = (key: MeasurementKey) => {
    const value = parseFloat(editValue.replace(',', '.'));
    const bodyFat = key === 'body_fat';
    if (!Number.isFinite(value) || value <= 0 || (bodyFat && (value < 2 || value > 70))) {
      toast({
        title: 'Invalid value',
        description: bodyFat ? 'Enter a body fat between 2 and 70 %.' : 'Enter a positive number.',
        variant: 'destructive',
      });
      return;
    }
    setStored(key, value);
    setEditingKey(null);
    setEditValue('');
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {trigger ?? (
          <button
            type="button"
            className="rounded-lg border border-border bg-card px-3 py-1.5 text-[11px] font-medium text-foreground transition-colors hover:bg-shell"
          >
            Review
          </button>
        )}
      </DialogTrigger>
      <DialogContent data-testid="feedback-dialog" className="max-h-[80vh] sm:max-w-[620px]">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold tracking-tight">
            Tape readings
            {/* Which scan, since it now opens from more than one list. */}
            <span data-testid="feedback-scan-date" className="tabular ml-2 text-xs font-normal text-muted-foreground">
              {new Date(measureData.createdAt).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </span>
          </DialogTitle>
          <DialogDescription className="text-[12.5px] leading-relaxed text-foreground-soft">
            Say who measured, then confirm each reading as the scan measured it,
            or correct it to what the tape said. Nothing is written until you
            confirm the changes.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[52vh] overflow-y-auto">
          <div
            data-testid="feedback-context"
            className="mb-2 grid gap-3 rounded-lg border border-border bg-shell/60 p-3 sm:grid-cols-2"
          >
            <fieldset className="flex flex-col gap-1.5">
              <legend className="mb-1.5 text-xs font-medium">Measured by</legend>
              <div className="flex gap-1.5" role="radiogroup">
                {(
                  [
                    ['coach', 'Me'],
                    ['other', 'Someone else'],
                  ] as const
                ).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={ctx.measuredBy === value}
                    onClick={() => editCtx({ measuredBy: value })}
                    className={`rounded-md border px-2.5 py-1.5 text-[11px] font-medium ${
                      ctx.measuredBy === value
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border bg-card text-foreground hover:bg-shell'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {ctx.measuredBy === 'other' && (
                <input
                  type="text"
                  value={ctx.measurerName}
                  maxLength={MAX_TEXT}
                  placeholder="Name"
                  aria-label="Name of who measured"
                  onChange={(e) => editCtx({ measurerName: e.target.value })}
                  className={TEXT_INPUT}
                />
              )}
            </fieldset>
            <ChoiceField
              label="Clothing"
              hint="optional"
              choices={CLOTHING_CHOICES}
              value={ctx.clothing}
              text={ctx.clothingText}
              onChange={(clothing, clothingText) => editCtx({ clothing, clothingText })}
            />
          </div>
          {keys.map((k) => {
            const key = k.key as MeasurementKey;
            const { original, stored, confirmed, corrected } = readingFor(k.key);
            const shown = stored ?? original;
            const editing = editingKey === k.key;

            return (
              <div
                key={k.key}
                className="flex items-center justify-between gap-3 border-b border-divider py-3 last:border-b-0"
              >
                <div className="min-w-0">
                  <div className="text-xs font-medium">{k.label}</div>
                  <div className="tabular text-[10px] text-muted-foreground">
                    {original !== undefined
                      ? `Scanned ${Math.round(original)} ${k.unit}`
                      : 'Not in this scan'}
                    {corrected && stored !== undefined && (
                      <>
                        {' · '}
                        <span className="text-[hsl(var(--projected))]">
                          corrected to {Math.round(stored)} {k.unit}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {editing ? (
                  <div className="flex shrink-0 items-center gap-2">
                    <input
                      type="number"
                      autoFocus
                      min={0}
                      step={1}
                      value={editValue}
                      aria-label={`${k.label} in ${k.unit}`}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') commitEdit(key);
                        if (e.key === 'Escape') setEditingKey(null);
                      }}
                      className="tabular w-20 rounded-md border border-border bg-card px-2 py-1 text-xs outline-none focus:border-primary"
                    />
                    <span className="text-[10px] text-muted-foreground">{k.unit}</span>
                    <ActionButton
                      variant="primary"
                      ariaLabel={`Save ${k.label}`}
                      onClick={() => commitEdit(key)}
                    >
                      Save
                    </ActionButton>
                    <ActionButton onClick={() => setEditingKey(null)}>Cancel</ActionButton>
                  </div>
                ) : (
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="tabular text-xs font-semibold">
                      {shown !== undefined ? `${Math.round(shown)} ${k.unit}` : '–'}
                    </span>
                    {confirmed || corrected ? (
                      <>
                        <Eyebrow tone={corrected ? 'projected' : 'primary'}>
                          {corrected ? 'Corrected' : 'Confirmed'}
                        </Eyebrow>
                        <ActionButton
                          onClick={() => {
                            clearStored(key);
                            toast({ title: `${k.label} reset to the scanned value` });
                          }}
                        >
                          Undo
                        </ActionButton>
                      </>
                    ) : (
                      <>
                        <ActionButton
                          ariaLabel={`Correct ${k.label}`}
                          onClick={() => {
                            setEditingKey(k.key);
                            // No scan value: start empty for the tape reading.
                            setEditValue(shown === undefined ? '' : Math.round(shown).toString());
                          }}
                        >
                          Correct
                        </ActionButton>
                        <ActionButton
                          variant="primary"
                          ariaLabel={`Confirm ${k.label}`}
                          disabled={original === undefined}
                          onClick={() => setStored(key, original as number)}
                        >
                          Confirm
                        </ActionButton>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* A reference body fat is a fitness reading; retail reviews tape only. */}
          {hasFeature('bodyFat') && (
            <BodyFatRow
              scanned={bodyFatScanned}
              stored={bodyFatAfter}
              editing={editingKey === 'body_fat'}
              editValue={editValue}
              onEditValue={setEditValue}
              onStartEdit={() => {
                setEditingKey('body_fat');
                setEditValue(bodyFatAfter === undefined ? '' : String(bodyFatAfter));
              }}
              onCommit={() => commitEdit('body_fat')}
              onCancel={() => setEditingKey(null)}
              onUndo={() => {
                clearStored('body_fat');
                editCtx({ bodyFatMethod: '', bodyFatMethodText: '' });
              }}
              method={ctx.bodyFatMethod}
              methodText={ctx.bodyFatMethodText}
              onMethod={(bodyFatMethod, bodyFatMethodText) => editCtx({ bodyFatMethod, bodyFatMethodText })}
            />
          )}
        </div>

        <DialogFooter>
          {/* DialogClose, not a second DialogTrigger. A trigger toggles; these
              two only ever dismiss, and one of them has to run its handler
              first. */}
          <DialogClose asChild>
            <ActionButton>Cancel</ActionButton>
          </DialogClose>
          {hasChanges && problem && (
            <p data-testid="feedback-problem" className="mr-auto self-center text-[11px] text-destructive">
              {problem}
            </p>
          )}
          <DialogClose asChild>
            <ActionButton
              variant="primary"
              disabled={!hasChanges || problem !== null}
              onClick={() => void save()}
            >
              Confirm changes
            </ActionButton>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
